import {
	useGetAcCategoriesQuery,
	useProductSearchQuery,
} from '@pco/api/generated/graphql';
import type {
	Category,
	CategoryList,
	SortBySelection,
} from '@pco/modules/CategoriesNavigation/index.types';
import {
	createSearchQueryFilters,
	createSortByForSearchQuery,
} from '@pco/modules/CategoriesNavigation/utils/categoryNavigation';

import { POPULAR_CATEGORIES } from '@pco/modules/CategoriesNavigation/utils/constants.impl';
import type {
	PartialParamVehicle,
	Vehicle,
} from '@pco/modules/VehicleSelection';
import type { ProductCardLocation } from '@pco/molecules/ProductCard/index.types';

const transformPopularCategories = (
	categories: CategoryList,
	activeCategoryPath: string,
): Category[] | undefined => {
	return (
		categories?.map((category) => {
			const url = new URL(window.location.href);
			const pathname = url.pathname.split('/');
			const base = pathname.splice(0, pathname.length - 1);
			return {
				label: category?.name || '',
				children: [
					{
						label: 'All',
						href: `${url.origin}${base.join('/')}/${category?.url_path}`,
						isActive: activeCategoryPath === category?.url_path,
					},
					...(category?.children?.map((category) => ({
						label: category?.name || '',
						href: `${url.origin}${base.join('/')}/${category?.url_key}`,
						isActive:
							category?.url_key?.includes(activeCategoryPath) &&
							activeCategoryPath !== 'popular',
					})) || []),
				],
			};
		}) || []
	);
};

const getPopularCategoryType = (
	categoryUrl: string,
): Extract<ProductCardLocation, 'popular' | 'clearance' | 'best-seller'> => {
	if (categoryUrl.includes('clearance')) {
		return 'clearance';
	}
	if (categoryUrl.includes('best-seller')) {
		return 'best-seller';
	}
	return 'popular';
};
const popularCategoryPathMap: Record<string, string> = {
	clearance: 'popular/popular-clearance',
	'popular-clearance': 'popular/popular-clearance',
	'best-seller': 'popular/popular-best-seller',
	'popular-best-seller': 'popular/popular-best-seller',
};

export const usePopularCategoriesNavigation = ({
	categoryUrlKey,
	pageNumber,
	pageSize,
	sortBySelection,
	selectedVehicle,
	paramVehicle,
}: {
	categoryUrlKey: string;
	pageSize: number;
	pageNumber: number;
	sortBySelection: SortBySelection;
	selectedVehicle?: Vehicle;
	paramVehicle?: PartialParamVehicle;
}) => {
	const isPopularCategory = POPULAR_CATEGORIES.includes(categoryUrlKey);
	const { isLoading, data: popularCategoriesData } = useGetAcCategoriesQuery(
		{
			url_key: 'popular',
		},
		{ enabled: isPopularCategory },
	);

	const { isFetching: isSearchFetching, data: popularProductsData } =
		useProductSearchQuery(
			{
				phrase: '',
				filter: createSearchQueryFilters({
					selectedCategory: popularCategoryPathMap[categoryUrlKey] ?? 'popular',
					selectedVehicle,
					paramVehicle,
				}),
				sort: createSortByForSearchQuery({
					userSelectedSortBy: [sortBySelection],
					selectedVehicle,
				}),
				pageSize: pageSize,
				page: Number(pageNumber) || 1,
			},
			{
				gcTime: 0,
				staleTime: 0,
				placeholderData: (prev) => prev,
				enabled: !isLoading && isPopularCategory,
			},
		);
	const popularCategories = transformPopularCategories(
		popularCategoriesData?.acCategories?.items as CategoryList,
		categoryUrlKey,
	);
	const popularType = getPopularCategoryType(categoryUrlKey);
	return {
		isLoading: isLoading || isSearchFetching,
		popularCategories,
		popularProductsData,
		isPopularCategory,
		popularType,
	};
};
